export type TravelOfferData = Record<string, string>

export type ImportedTravelOffer = {
	id: string
	rowNumber: number
	data: TravelOfferData
}

export type ParsedTravelOffers = {
	headers: string[]
	offers: ImportedTravelOffer[]
}

const REQUIRED_HEADERS = [
	"destination",
	"land",
	"afrejsedato",
	"hjemrejsedato",
	"flypris_2_voksne_dkk",
	"hotelnavn",
	"samlet_pris_dkk",
	"pris_pr_person_dkk",
]

function parseTsvRows(input: string) {
	const rows: string[][] = []
	let row: string[] = []
	let field = ""
	let quoted = false

	for (let index = 0; index < input.length; index += 1) {
		const character = input[index]

		if (character === "\"" && (quoted || !field)) {
			if (quoted && input[index + 1] === "\"") {
				field += "\""
				index += 1
			} else {
				quoted = !quoted
			}
			continue
		}

		if (character === "\t" && !quoted) {
			row.push(field)
			field = ""
			continue
		}

		if ((character === "\n" || character === "\r") && !quoted) {
			if (character === "\r" && input[index + 1] === "\n") index += 1
			row.push(field)
			rows.push(row)
			row = []
			field = ""
			continue
		}

		field += character
	}

	if (quoted) {
		throw new Error("TSV-filen indeholder et tekstfelt med et manglende afsluttende anførselstegn.")
	}

	if (field || row.length) {
		row.push(field)
		rows.push(row)
	}

	return rows
}

function createOfferId(rowNumber: number, data: TravelOfferData) {
	const identity = [data.destination, data.afrejsedato, data.hotelnavn]
		.filter(Boolean)
		.join("-")
		.toLocaleLowerCase("da-DK")
		.replace(/[^a-z0-9æøå]+/gi, "-")
		.replace(/^-|-$/g, "")

	return `travel-offer-${rowNumber}-${identity || "tilbud"}`
}

export function parseTravelOfferTsv(input: string): ParsedTravelOffers {
	const rows = parseTsvRows(String(input || "").replace(/^\uFEFF/, ""))
	const nonEmptyRows = rows.filter((row) => row.some((value) => value.trim()))

	if (nonEmptyRows.length < 2) {
		throw new Error("TSV-filen skal indeholde en kolonneoverskrift og mindst ét rejsetilbud.")
	}

	const headers = nonEmptyRows[0].map((header) => header.trim())
	const duplicateHeaders = headers.filter((header, index) => header && headers.indexOf(header) !== index)
	if (duplicateHeaders.length) {
		throw new Error(`TSV-filen indeholder kolonnen "${duplicateHeaders[0]}" mere end én gang.`)
	}

	const missingHeaders = REQUIRED_HEADERS.filter((header) => !headers.includes(header))
	if (missingHeaders.length) {
		throw new Error(`TSV-filen mangler følgende kolonner: ${missingHeaders.join(", ")}.`)
	}

	const offers = nonEmptyRows.slice(1).map((values, index) => {
		const rowNumber = index + 2
		if (values.length > headers.length) {
			throw new Error(`Række ${rowNumber} har flere felter end kolonneoverskriften.`)
		}

		const data = headers.reduce<TravelOfferData>((offer, header, headerIndex) => {
			offer[header] = String(values[headerIndex] || "").trim()
			return offer
		}, {})

		return {
			id: createOfferId(rowNumber, data),
			rowNumber,
			data,
		}
	})

	return { headers, offers }
}
